import { useMemo, useState, useRef, useEffect } from 'react';
import { horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { AnimatePresence, motion } from 'motion/react';
import { MessagesHooks } from '@frontend/api-messaging';
import { SMSDataSharedUtils } from '@frontend/api-sms-data';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { InboxTemplateResponsiveModal, TemplatesPopover, useInboxTemplateFlows } from '@frontend/inbox-templates';
import { CHAT_HEIGHTS, MiniChatProps, useMiniChatStore, WIDTHS } from '@frontend/mini-chat-store';
import { FeatureUpgradeModalWrapper } from '@frontend/self-upgrade';
import {
  ThreadSendingArea,
  SignatureButton,
  ImageButton,
  SmartDraftButton,
  useSmartDraft,
  useThreadSendingArea,
} from '@frontend/thread-sending-area';
import { EmojiButton, NakedButton, useTooltip } from '@frontend/design-system';
import { MiniChatBody } from './mini-chat-body';
import { MiniChatHeader } from './mini-chat-header';
import { styles } from './styles';

export const MiniChat = ({
  chatId,
  groupId,
  contextPersonId,
  personPhone,
  onSelectPersonPhone,
  onSelectLocationPhone,
  unreadCount,
  sizeVariant,
  onMaximize,
  onMinimize,
  onClose,
  threadId,
  focusThreadItemData,
  canSchedule,
  locationPhone,
  dragIsActive,
  isMobile,
  isNew,
  ...rest
}: MiniChatProps) => {
  const { updateChat } = useMiniChatStore();
  const [placeholderText, setPlaceHolderText] = useState<string | undefined>(undefined);
  const { t } = useTranslation('inbox-mini-chat');

  const { getThreadSMSItems } = SMSDataSharedUtils.threadItemsUtils;

  // Get thread messages for smart draft
  const { threadItems, refetchThread, metadata } = MessagesHooks.useThread({
    threadId: threadId ?? '',
    groupId,
    providedPersonPhone: personPhone,
    isNew,
  });

  // Filter messages for smart draft (last 5 messages with body)
  const smartDraftMessages = useMemo(() => {
    return getThreadSMSItems(threadItems)
      .filter((sms) => sms.body)
      .slice(-5);
  }, [threadItems]);

  const isEmptyThread = threadItems.length === 0;

  // Track current isEmpty state in a ref so setTimeout can read the latest value
  // This prevents the closure from capturing a stale isEmpty value
  const isEmptyThreadRef = useRef(isEmptyThread);
  useEffect(() => {
    isEmptyThreadRef.current = isEmptyThread;
  }, [isEmptyThread]);

  const {
    smartDraft,
    showSmartDraftButton,
    showSmartDraftButtonNoBorder,
    isLoadingSmartDraft,
    showSmartDraftResult,
    generateSmartDraft,
    redoSmartDraft,
    acceptSmartDraft,
    closeSmartDraft,
  } = useSmartDraft({
    smartDraftMessages,
    threadId,
    aiBotStatusActive: metadata?.aiStatus?.isActive,
  });

  const {
    tooltipProps: viewAllTemplatesTooltipProps,
    triggerProps: viewAllTemplatesTriggerProps,
    Tooltip: ViewAllTemplatesTooltip,
  } = useTooltip({ placement: 'top' });

  const {
    tooltipProps: quickInsertTooltipProps,
    triggerProps: quickInsertTriggerProps,
    Tooltip: QuickInsertTooltip,
  } = useTooltip({ placement: 'top' });

  const { focusTextarea, insertText, setBody, templatePopoverState, ...threadSendingAreaState } = useThreadSendingArea({
    threadId,
    groupId,
    personPhone,
    contextPersonId: contextPersonId,
    uiType: 'mini-chat',
    locationPhone,
    sendSideEffects: {
      onSuccess: (res) => {
        if (res.threadId !== threadId || res.locationPhone !== locationPhone || res.locationId !== groupId) {
          updateChat(chatId, {
            threadId: res.threadId,
            locationPhone: res.locationPhone,
            groupId: res.locationId,
            isNew: false,
          });
        } else if (isEmptyThread) {
          updateChat(chatId, { isNew: false });
          // Give BE time to create new thread before refetching
          setTimeout(() => {
            // Check if thread is still empty when timeout fires
            // Use ref to get current isEmpty state, avoiding stale closure value
            if (isEmptyThreadRef.current) {
              refetchThread();
            }
          }, 300);
        }
      },
    },
  });
  const {
    availableTemplateFlows,
    modalControl,
    person,
    isPrimaryContact,
    canChangeContextContact,
    templateTypeProps,
    formPromotionModalProps,
  } = useInboxTemplateFlows({
    groupId,
    contextPersonId,
    personPhone,
    locationPhone: locationPhone ?? '',
    onRenderTemplate: ({ bodyValue }) => {
      setBody(bodyValue);
      focusTextarea();
    },
  });

  const smartDraftResultBarProps = useMemo(
    () => ({
      text: smartDraft,
      onTextClick: () => {
        // Text click no longer inserts — only shows preview via hover
      },
      onAccept: () => {
        focusTextarea();
        setBody(smartDraft);
        acceptSmartDraft();
        setPlaceHolderText(undefined);
      },
      onTextMouseOver: () => setPlaceHolderText(smartDraft),
      onTextMouseLeave: () => setPlaceHolderText(undefined),
      onRedo: () => {
        setPlaceHolderText(undefined);
        redoSmartDraft();
      },
      onClose: () => {
        closeSmartDraft();
        setPlaceHolderText(undefined);
      },
    }),
    [smartDraft, focusTextarea, setBody, acceptSmartDraft, closeSmartDraft, redoSmartDraft]
  );

  const { attributes, listeners, setNodeRef, setActivatorNodeRef } = useSortable({
    id: chatId,
    strategy: horizontalListSortingStrategy,
    disabled: dragIsActive,
  });

  const [isChangingContextPerson, setIsChangingContextPerson] = useState(false);

  return (
    <>
      <motion.div
        ref={setNodeRef}
        variants={{
          full: {
            height: CHAT_HEIGHTS['full'],
            width: WIDTHS['full'],
          },
          minimized: {
            height: CHAT_HEIGHTS['minimized'],
            width: WIDTHS['minimized'],
          },
          avatar: {
            height: CHAT_HEIGHTS['avatar'],
            width: WIDTHS['avatar'],
          },
        }}
        initial='avatar'
        animate={sizeVariant}
        exit={sizeVariant === 'full' ? 'minimized' : sizeVariant}
        css={styles.miniChatContainerStyle}
        data-chatid={chatId}
        data-threadid={threadId}
        data-trackingid='mini-chat-container'
        {...rest}
      >
        <div css={styles.miniChatContentStyle}>
          <AnimatePresence>
            <MiniChatHeader
              key={'mini-chat-header' + chatId}
              chatId={chatId}
              groupId={groupId}
              personPhone={personPhone}
              onSelectPersonPhone={onSelectPersonPhone}
              locationPhone={locationPhone}
              onSelectLocationPhone={onSelectLocationPhone}
              contextPersonId={contextPersonId}
              unreadCount={unreadCount}
              sizeVariant={sizeVariant}
              onMaximize={onMaximize}
              onMinimize={onMinimize}
              onClose={onClose}
              hideButtons={dragIsActive}
              ref={setActivatorNodeRef}
              isMobile={isMobile}
              isSelectingContextContact={isChangingContextPerson}
              setIsSelectingContextContact={setIsChangingContextPerson}
              threadId={threadId}
              isNew={isNew}
              {...listeners}
              {...attributes}
            />
            {sizeVariant === 'full' && !dragIsActive ? (
              <MiniChatBody
                key={'mini-chat-body' + chatId}
                groupId={groupId}
                threadId={threadId}
                focusThreadItemData={focusThreadItemData}
                personPhone={personPhone}
                unreadCount={unreadCount}
                chatId={chatId}
                isNew={isNew}
              />
            ) : (
              <div key='mini-chat-body-placeholder' css={styles.miniChatBodyPlaceholderStyle}></div>
            )}
            <ThreadSendingArea
              {...threadSendingAreaState}
              canSchedule={canSchedule}
              autoGrowTextareaProps={{
                maxRows: 3,
                minRows: 3,
              }}
              hideTypingIndicator={sizeVariant !== 'full'}
              buttonType='button'
              placeholderText={placeholderText || undefined}
            >
              <div css={styles.sendingAreaActionButtonsStyle}>
                <SignatureButton
                  size='large'
                  css={[styles.iconButtonBaseStyle, threadSendingAreaState.disabled && styles.iconButtonDisabledStyle]}
                  groupId={groupId}
                  variant='secondary'
                  disabled={threadSendingAreaState.disabled}
                />
                <ImageButton
                  size='large'
                  css={[styles.iconButtonBaseStyle, threadSendingAreaState.disabled && styles.iconButtonDisabledStyle]}
                  addImages={threadSendingAreaState.addMedia}
                  variant='secondary'
                  disabled={threadSendingAreaState.disabled}
                />
                <EmojiButton
                  // styles necessary to match this button to the sizing required by mini-chat designs
                  css={styles.emojiButtonStyle}
                  onSelect={({ emoji, unified }) => {
                    if (unified !== '') {
                      insertText(emoji);
                    }
                  }}
                  disabled={threadSendingAreaState.disabled}
                />
                <div
                  css={[
                    styles.templateButtonGroupBaseStyle,
                    !threadSendingAreaState.disabled && styles.templateButtonGroupHoverStyle,
                  ]}
                >
                  {/* Left: View All Templates */}
                  <NakedButton
                    onClick={() => modalControl.openModal()}
                    disabled={threadSendingAreaState.disabled}
                    trackingId='mini-chat-templates-view-all'
                    css={[
                      styles.templateButtonStyle,
                      styles.templateButtonHoverStyle,
                      threadSendingAreaState.disabled && styles.templateButtonDisabledStyle,
                    ]}
                    {...(!templatePopoverState.isOpen ? viewAllTemplatesTriggerProps : {})}
                  >
                    <Icon name='template' color={threadSendingAreaState.disabled ? 'disabled' : 'default'} />
                  </NakedButton>

                  {/* Right: Quick Insert Last Used */}
                  <div
                    data-trackingid='mini-chat-templates-quick-insert-wrapper'
                    {...(!templatePopoverState.isOpen ? quickInsertTriggerProps : {})}
                    css={[
                      styles.templateQuickInsertDividerStyle,
                      !threadSendingAreaState.disabled && styles.templateQuickInsertDividerHoverStyle,
                    ]}
                  >
                    <NakedButton
                      disabled={threadSendingAreaState.disabled}
                      trackingId='mini-chat-templates-quick-insert'
                      css={[
                        styles.templateQuickInsertButtonStyle,
                        threadSendingAreaState.disabled && styles.templateButtonDisabledStyle,
                      ]}
                      {...templatePopoverState.getTriggerProps()}
                    >
                      <Icon
                        name='caret-down-tiny'
                        size={8}
                        color={threadSendingAreaState.disabled ? 'disabled' : 'default'}
                        css={[
                          styles.templateCaretIconStyle,
                          templatePopoverState.isOpen && styles.templateCaretIconOpenStyle,
                        ]}
                      />
                    </NakedButton>
                  </div>

                  {!templatePopoverState.isOpen && (
                    <>
                      <ViewAllTemplatesTooltip {...viewAllTemplatesTooltipProps}>
                        {t('View All Templates')}
                      </ViewAllTemplatesTooltip>
                      <QuickInsertTooltip {...quickInsertTooltipProps}>
                        {t('Quick Insert Last Used')}
                      </QuickInsertTooltip>
                    </>
                  )}
                </div>
                {!threadSendingAreaState.canSendSMS &&
                  (showSmartDraftButton || isLoadingSmartDraft || showSmartDraftResult) && (
                    <SmartDraftButton
                      onClick={generateSmartDraft}
                      onInsert={() => {
                        focusTextarea();
                        setBody(smartDraft);
                        acceptSmartDraft();
                        setPlaceHolderText(undefined);
                      }}
                      disabled={threadSendingAreaState.disabled}
                      isLoading={isLoadingSmartDraft}
                      noBorder={showSmartDraftButtonNoBorder}
                      usePopoverMode
                      mobilePopoverProps={showSmartDraftResult ? smartDraftResultBarProps : undefined}
                      compact
                      onHoverPreview={smartDraft ? () => setPlaceHolderText(smartDraft) : undefined}
                      onLeavePreview={() => setPlaceHolderText(undefined)}
                    />
                  )}
              </div>
            </ThreadSendingArea>
          </AnimatePresence>
        </div>
      </motion.div>
      <InboxTemplateResponsiveModal modalControl={modalControl} templateTypeProps={templateTypeProps} person={person} />
      <TemplatesPopover
        getMenuProps={templatePopoverState.getMenuProps}
        getItemProps={templatePopoverState.getItemProps}
        templateItems={availableTemplateFlows}
        locationId={groupId}
        closePopover={templatePopoverState.close}
        person={person}
        isPrimaryContact={isPrimaryContact}
        onChangeContextPersonClick={
          canChangeContextContact
            ? () => {
                setIsChangingContextPerson(true);
              }
            : undefined
        }
      />
      <FeatureUpgradeModalWrapper {...formPromotionModalProps} />
    </>
  );
};
